/**
 * SidePanel Component
 *
 * This component renders the main navigation sidebar of the application.
 * It handles role-based menu rendering, responsive behavior, and menu state management.
 *
 * Key Features:
 * - Role-based menu visibility (group_member, group_admin, account_admin, platform_admin)
 * - Responsive design (auto-minimize on mobile, hide on phone form factor)
 * - Dynamic menu rendering based on user permissions
 * - Direct menu items for users with limited permissions (UX optimization)
 * - Expandable/collapsible menu sections for admin users
 *
 * @param {Function} onMenuSelect - Callback when a menu item is selected
 * @param {Function} onToggleMinimize - Callback when panel is minimized/expanded
 * @param {Function} setHeroContent - Function to update the main content area
 * @param {boolean} isSessionTimedOut - Flag indicating if user session has expired
 */

import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import "./css/SidePanel.css"; // Updated import path
import { useUser } from "./context/UserContext";
import { useSubscription } from "./context/SubscriptionContext";
import { useEdition } from "./context/EditionContext";
import { useRoleCheck } from "../../hooks/useRoleCheck";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { clearFrontendAuthData } from "./utils/AuthUtil";
import Tooltip from "./common/Tooltip"; // Tooltip component for minimized menu items
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Import FontAwesomeIcon
import {
  faUserCog,
  faCommentDots,
  faCreditCard,
  faClipboard,
  faDownload,
  faUsers,
  faKey,
  faEnvelope, // Added email icon
  faSms, // SMS icon
  faEnvelopeOpenText, // Generate emails icon
  faUserShield, // Account Management icon
  faChartPie, // Simulation Heuristics icon
  faHistory, // Recent Simulations icon
  faFileText, // Generate Email Report icon
  faCalculator, // Generate Risk Scores icon
  faChartBar, // Reports section icon
  faCrown, // Platform Admin icon
  faUser, // Account icon
  faShield, // Shield icon for Scam Detector
  faCalendarPlus, // Create Simulation Schedule icon
  faPlay, // Execute Simulation Schedule icon
  faEdit, // Generate Blogs icon
  faTachometerAlt, // Admin Dashboard icon
  faShareAlt, // Social Media icon
  faPhone, // Phone icon for Check a Phone Number
  faFish, // Fish hook icon for Simulations menu
  faExclamationTriangle, // Warning triangle icon
  faBullhorn, // Manage Announcements icon
  faGraduationCap, // Resources icon
  faBook, // Alerts Intelligence Hub icon
  faComments, // Comments icon for Text Conversation
  faNewspaper, // Recent Alerts icon
  faTheaterMasks, // Role Play icon
  faRobot, // Robot icon for Automated Analysis
  faLink, // Link icon for URL scanning
  faShieldAlt, // Shield icon for URL Safety
  faImage, // Image icon for Verify a Picture
  faToolbox, // Toolbox icon for More Tools
  faFileContract, // Cyber-Insurance Readiness report icon
} from "@fortawesome/free-solid-svg-icons"; // Import specific icons
import ConsentAPI from "./services/ConsentAPI"; // Import ConsentAPI for verification functions
import UserAPI from "./services/UserAPI"; // Import UserAPI for user data
import SimulationScheduleAPI from "./services/SimulationScheduleAPI"; // Import SimulationScheduleAPI for simulation schedule functions
import { useTranslation } from "../../i18n/LanguageContext"; // i18n hook for English/Spanish UI labels

/**
 * MENU_ITEMS Configuration
 *
 * This object defines all menu items and their corresponding icons for each menu section.
 * RESTRUCTURED: Hybrid approach with content-based grouping and method indicators
 *
 * IMPORTANT: The menu name strings defined here MUST match the component names
 * in Hero.js, as Hero component renders content based on these exact strings.
 *
 * Menu Structure (Hybrid Approach):
 * - SCAM_DETECT_AI_MENU: Quick Scan (primary action)
 * - EMAIL_ANALYSIS_MENU: Forwarded Emails (automated) + Manual Entry
 * - TEXT_ANALYSIS_MENU: Forwarded Texts (automated) + Manual Input + Text Conversation + Check a Phone Number
 * - SOCIAL_PROTECTION_MENU: URL-based + Manual details entry
 * - USER_MENU: Basic user management features
 * - ADMIN_MENU: Account management features (requires account_admin+ role)
 * - SIMULATIONS_MENU: Simulation-related features
 * - REPORTS_MENU: Analytics and reporting features
 * - PLATFORM_ADMIN_MENU: Platform administration features (requires platform_admin role)
 * - EDUCATION_RESOURCES_MENU: Educational content and resources
 */
// Note that the menu name strings below, such as "Create User", "Simulation Messages" are specified
// here in the SidePanel component and must be matched in the Hero.js.
// The Hero component is responsible for rendering the
// selected component based on the content prop passed from the SidePanel.
const MENU_ITEMS = {
  // Basic user management menu - available to all authenticated users
  USER_MENU: [
    { name: "Member User Info", icon: faUserCog }, // Always available
    { name: "Groups & Organizations", icon: faUsers }, // Platform admin only
    { name: "Resend Verification Key", icon: faKey }, // Group admin only
  ],

  // Account management menu - requires account_admin or higher permissions
  ADMIN_MENU: [{ name: "Account & Billing", icon: faUser }],

  // Simulations menu - available to all authenticated users with role-based filtering
  SIMULATIONS_MENU: [
    { name: "Simulation Profile", icon: faClipboard }, // Always available
    { name: "Simulation Messages", icon: faChartPie }, // Platform admin only
    { name: "Recent Simulations", icon: faHistory }, // Always available
  ],

  // Reports menu - available to all with role-based filtering
  REPORTS_MENU: [
    { name: "Shield Reports", icon: faShieldAlt }, // Personal Shield Reports - Available to all users
    { name: "Cyber-Insurance Readiness", icon: faFileContract }, // Corporate account admins only
    { name: "Export Data", icon: faDownload }, // Account admin only
  ],

  // Platform administration menu - platform_admin role only
  PLATFORM_ADMIN_MENU: [
    { name: "Admin Dashboard", icon: faTachometerAlt },
    { name: "Scam Reports", icon: faExclamationTriangle },
    { name: "User Subscription Audit", icon: faCreditCard },
    { name: "Events", icon: faCalendarPlus },
    { name: "Send Email", icon: faEnvelope },
    { name: "Send SMS", icon: faSms }, // Coming soon feature
    { name: "Generate Blogs", icon: faEdit },
    { name: "Generate Simulation Data", icon: faRobot },
    { name: "Manage Announcements", icon: faBullhorn },
    { name: "Create Simulation Schedule", icon: faCalendarPlus },
    { name: "Execute Simulation Schedule", icon: faPlay },
  ],

  // Demo user management menu - platform_admin role only
  DEMO_MENU: [
    { name: "Demo Users", icon: faUserShield },
    { name: "Event Demo Seats", icon: faCalendarPlus },
    { name: "Test Users", icon: faUserShield },
  ],

  // AI-powered scam detection menu - available to all authenticated users
  // Reorganized with hybrid approach: content-grouped with method indicators
  SCAM_DETECT_AI_MENU: [],

  // Email Analysis submenu
  EMAIL_ANALYSIS_MENU: [
    { name: "Forwarded Emails", icon: faRobot }, // Automated analysis history
    { name: "Manual Entry", icon: faEdit }, // Manual paste content
  ],

  // Text/SMS Analysis submenu
  TEXT_ANALYSIS_MENU: [
    { name: "Forwarded Texts", icon: faRobot }, // Automated analysis history
    { name: "Manual Input", icon: faEdit }, // Manual paste message
    { name: "Text Conversation", icon: faComments }, // Conversation history analysis
  ],

  // Social Media Protection submenu
  SOCIAL_PROTECTION_MENU: [
    { name: "Business/Person Check", icon: faEdit }, // Manual details entry
    { name: "Social Media Posts", icon: faShieldAlt }, // URL safety analysis
  ],

  // More Tools submenu - specialized analysis tools
  ADVANCED_TOOLS_MENU: [
    { name: "Verify a Picture", icon: faImage }, // Image/screenshot analysis
    { name: "Check a Phone Number", icon: faPhone }, // Phone number lookup
    // QR Code Check is mobile-only (see HeaderAppMobile.js)
  ],

  // Resources menu - available to all authenticated users
  EDUCATION_RESOURCES_MENU: [
    { name: "Training Center", icon: faGraduationCap },
    { name: "Alerts Intelligence Hub", icon: faBook },
    // { name: "Recent Alerts", icon: faNewspaper },
  ],
};

/**
 * Main SidePanel Component
 *
 * Manages the sidebar navigation state and menu rendering based on user roles.
 * Implements responsive behavior and role-based access control.
 */
function SidePanel({
  onMenuSelect,
  onToggleMinimize,
  setHeroContent, // New prop to update Hero content
  isSessionTimedOut, // New prop to handle session timeout
  currentContent, // New prop to track current hero content for highlighting
}) {
  // =========================================================================
  // CONTEXT AND HOOKS
  // =========================================================================

  // User context for accessing logged-in user data and selected user
  const { selectedUser, loggedInUser, isDemoSeat } = useUser(); // Use UserContext

  // i18n: translate navigation labels (English keys, Spanish/English values)
  const { t } = useTranslation();

  // Subscription context for checking subscription status
  const { hasActiveSubscription, subscription, isInGracePeriod } =
    useSubscription();

  // Edition context — corporate vs consumer (gates corporate-only nav items)
  const { isCorporate } = useEdition();

  // Feature access control - combines user activation and subscription checks
  const {
    canAccessFunctionalFeatures,
    getAccessDenialReason,
    isDeactivated,
    needsSubscription,
  } = useFeatureAccess();

  const formatDateForBanner = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const graceEndsOn = formatDateForBanner(
    subscription?.graceEndDate || subscription?.endDate,
  );

  // Role checking hook for permission-based UI rendering
  const {
    canAccessAccountManagement,
    isPlatformAdmin,
    canManageGroups,
    isGroupAdmin,
    refreshRoles,
  } = useRoleCheck(loggedInUser); // Add role checking hook with trigger

  // Only account/platform admins can take billing actions.
  const canManageBilling = canAccessAccountManagement();

  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  // Menu expansion state for different sections
  const [isTopMenuExpanded, setIsTopMenuExpanded] = useState(false);
  const [isCollapsed, setIsMinimized] = useState(false);
  // Temporary overlay expansion while hovering a collapsed panel
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const isMinimized = isCollapsed && !isHoverExpanded;
  const [isAdminMenuExpanded, setIsAdminMenuExpanded] = useState(false); // New state for Account menu
  const [isPlatformAdminExpanded, setIsPlatformAdminExpanded] = useState(false); // New state for Platform Admin menu
  const [isDemoExpanded, setIsDemoExpanded] = useState(false); // State for Demo menu
  const [isSimulationsExpanded, setIsSimulationsExpanded] = useState(false); // New state for Simulations menu
  const [isReportsExpanded, setIsReportsExpanded] = useState(false); // New state for Reports menu
  const [isScamDetectAIExpanded, setIsScamDetectAIExpanded] = useState(false); // New state for Analysis Tools menu
  const [isEducationResourcesExpanded, setIsEducationResourcesExpanded] =
    useState(false); // New state for Education & Resources menu

  // New state for Protection submenus in the restructured Scam Detector menu
  const [isEmailProtectionExpanded, setIsEmailProtectionExpanded] =
    useState(false);
  const [isTextProtectionExpanded, setIsTextProtectionExpanded] =
    useState(false);
  const [isSocialProtectionExpanded, setIsSocialProtectionExpanded] =
    useState(false);
  const [isAdvancedToolsExpanded, setIsAdvancedToolsExpanded] = useState(false);

  // Authentication and user status tracking
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userVerificationStatus, setUserVerificationStatus] = useState(null); // Track verification status
  const [isCheckingVerification, setIsCheckingVerification] = useState(false); // Track verification check status
  const [isManuallyMinimized, setIsManuallyMinimized] = useState(false); // Track if user manually minimized

  // Active menu item tracking for highlighting
  const [activeMenuItem, setActiveMenuItem] = useState(null); // Track currently active menu item

  // Use ref to store the toggle callback to prevent infinite re-renders
  const toggleMinimizeRef = useRef(onToggleMinimize);

  // =========================================================================
  // EFFECT HOOKS FOR COMPONENT INITIALIZATION
  // =========================================================================

  // Update ref when callback changes
  useEffect(() => {
    toggleMinimizeRef.current = onToggleMinimize;
  }, [onToggleMinimize]);

  /**
   * Authentication Status Monitor
   * Checks if user is authenticated via UID cookie and updates state accordingly
   * Also listens for login/logout events from UserContext
   */
  useEffect(() => {
    let isHandlingLogout = false; // Guard to prevent infinite loop

    // Function to check if user is authenticated via UID cookie
    const checkAuthStatus = () => {
      const cookies = document.cookie.split(";");
      const uuidCookie = cookies.find((cookie) =>
        cookie.trim().startsWith("uuid="),
      );
      const authenticated =
        !!uuidCookie && uuidCookie.split("=")[1].trim() !== "";

      // If authentication state changed from true to false (user logged out or session expired)
      if (isAuthenticated && !authenticated && !isHandlingLogout) {
        console.log(
          "SidePanel: Authentication lost, calling clearFrontendAuthData",
        );
        isHandlingLogout = true; // Set guard
        clearFrontendAuthData(); // This will trigger the callback which calls handleAuthenticationLoss
      }

      setIsAuthenticated(authenticated);

      // Reset guard if user is authenticated
      if (authenticated) {
        isHandlingLogout = false;
      }
    };

    // Check authentication status on mount
    checkAuthStatus();

    // Listen for the custom login event from UserContext
    const handleLogin = () => {
      checkAuthStatus();
    };

    // Listen for UserContext login events
    window.addEventListener("userLoggedIn", handleLogin);
    window.addEventListener("userLoggedOut", handleLogin); // Also handle logout

    // Also check when storage changes (for handling signout)
    window.addEventListener("storage", checkAuthStatus);

    return () => {
      window.removeEventListener("userLoggedIn", handleLogin);
      window.removeEventListener("userLoggedOut", handleLogin);
      window.removeEventListener("storage", checkAuthStatus);
    };
  }, [isAuthenticated]);

  /**
   * Responsive Behavior Handler
   * Manages panel minimization based on screen size and user interactions
   */
  useEffect(() => {
    const handleResize = () => {
      // Check if device is in phone form factor
      const isPhoneFormFactor = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const aspectRatio = height / width;
        return width < 768 && aspectRatio > 1.3;
      };

      // If switched to phone form factor and not minimized, minimize
      if (isPhoneFormFactor() && !isCollapsed) {
        setIsMinimized(true);
        setIsManuallyMinimized(false); // Reset manual flag when forced to minimize
        // Parent will be notified via useEffect
      }
    };

    window.addEventListener("resize", handleResize);

    // Check on mount
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isCollapsed]); // Removed onToggleMinimize to prevent infinite re-renders

  /**
   * Hero Content Manager
   * Checks for requested hero content in localStorage and switches to it
   * This enables deep linking and direct navigation to specific components
   * Also manages active menu item state when content changes externally
   */
  useEffect(() => {
    // Function to check and process requested hero content
    const checkRequestedContent = () => {
      const requestedContent = localStorage.getItem("requestedHeroContent");
      if (requestedContent) {
        console.log(
          "🔄 SidePanel: Switching to requested content:",
          requestedContent,
        );
        setHeroContent(requestedContent);

        // The currentContent useEffect will handle the highlighting logic
        // when setHeroContent triggers a content change

        // Clear the requested content after using it
        localStorage.removeItem("requestedHeroContent");
      }
    };

    // Check on mount
    checkRequestedContent();

    // Listen for custom heroContentRequested event (triggered by Alerts.js when clicking internal links)
    // Note: storage events only fire in OTHER windows/tabs, so we use a custom event for same-window communication
    const handleHeroContentRequested = (event) => {
      console.log(
        "🔄 SidePanel: heroContentRequested event received:",
        event.detail,
      );
      checkRequestedContent();
    };

    window.addEventListener("heroContentRequested", handleHeroContentRequested);

    return () => {
      window.removeEventListener(
        "heroContentRequested",
        handleHeroContentRequested,
      );
    };
  }, [setHeroContent]);

  /**
   * Content Synchronization Effect
   * Syncs the active menu item when content changes externally (not through menu clicks)
   * This ensures proper highlighting when content is changed by other components
   * and clears highlighting when content is set by non-SidePanel sources (like Header)
   */
  useEffect(() => {
    // Always start by clearing the active menu item
    let menuItemToHighlight = null;

    if (currentContent) {
      // Find the menu item that corresponds to the current content
      const findMenuItemForContent = (content) => {
        // Direct matches for content that maps to different menu items
        if (content === "Social Media Scams") return "Scan by URL"; // Updated for new structure
        if (content === "Check a Phone Number") return "Check a Phone Number";
        if (content === "Text Message Analysis") return "Manual Entry"; // Updated for new structure
        if (content === "UserList") return "User List";
        if (content === "Subscriptions") return "Account & Billing";
        if (content === "Account Info") return "Account & Billing";

        // Check if it's a "Coming Soon" feature
        if (content.startsWith("Coming Soon:")) {
          return content.replace("Coming Soon:", "");
        }

        // Check if content matches any menu item exactly
        const allMenuItems = [
          ...MENU_ITEMS.USER_MENU,
          ...MENU_ITEMS.ADMIN_MENU,
          ...MENU_ITEMS.SIMULATIONS_MENU,
          ...MENU_ITEMS.REPORTS_MENU,
          ...MENU_ITEMS.PLATFORM_ADMIN_MENU,
          ...MENU_ITEMS.SCAM_DETECT_AI_MENU,
          ...MENU_ITEMS.EMAIL_ANALYSIS_MENU,
          ...MENU_ITEMS.TEXT_ANALYSIS_MENU,
          ...MENU_ITEMS.SOCIAL_PROTECTION_MENU,
          ...MENU_ITEMS.ADVANCED_TOOLS_MENU,
          ...MENU_ITEMS.EDUCATION_RESOURCES_MENU,
        ].map((item) => item.name);

        if (allMenuItems.includes(content)) {
          return content;
        }

        // For content that doesn't match a menu item (like Dashboard, How do I...?,
        // Notifications, Account Info, Settings, etc. from Header), return null
        return null;
      };

      menuItemToHighlight = findMenuItemForContent(currentContent);
    }

    // Set the highlighting (or clear it if no match found)
    setActiveMenuItem(menuItemToHighlight);

    console.log(
      `SidePanel: Content changed to "${currentContent}", highlighting menu item: "${
        menuItemToHighlight || "none"
      }"`,
    );
  }, [currentContent]);

  /**
   * Auto-Minimization Logic
   * Automatically minimizes panel based on device type, authentication status,
   * and available menu permissions for optimal UX
   */
  useEffect(() => {
    // Check if device is in phone form factor (aspect ratio typically > 1.5 and width < 768)
    const isPhoneFormFactor = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = height / width;
      return width < 768 && aspectRatio > 1.3; // Phone-like dimensions
    };

    // If not authenticated, minimize the panel
    if (!isAuthenticated) {
      if (!isCollapsed) {
        setIsMinimized(true);
        setIsManuallyMinimized(false); // Reset manual flag when forced to minimize
        // Parent will be notified via useEffect
      }
      return;
    }

    // For mobile/phone form factor, hide the side panel completely
    // The hamburger menu in Header will handle navigation
    if (isPhoneFormFactor()) {
      if (!isCollapsed) {
        setIsMinimized(true);
        setIsManuallyMinimized(false); // Reset manual flag when forced to minimize
        // Parent will be notified via useEffect
      }
      return;
    }

    // For desktop: keep side panel expanded when user is authenticated
    // Only minimize if there are truly no visible menu sections
    const hasUserMenu = true; // User Management menu is always available when authenticated
    const hasAccountManagement = canAccessAccountManagement(); // Only visible with proper permissions
    const hasSimulations = true; // Simulations menu is always available when authenticated
    const hasReports = true; // Reports menu is always available when authenticated
    const hasScamDetect = true; // Analysis Tools menu is always available when authenticated
    const hasEducationResources = true; // Education & Resources menu is always available when authenticated
    const hasPlatformAdmin = isPlatformAdmin(); // Only visible with platform_admin role

    // Check if user has Groups & Organizations permission for User menu
    const hasGroupsPermission = isPlatformAdmin();
    const hasVisibleUserMenuItems = hasGroupsPermission || true; // Member User Info and Resend Key are always available

    // Determine if there are actually visible menu sections
    const hasVisibleMenuSections =
      (hasUserMenu && hasVisibleUserMenuItems) || // User Management section
      hasAccountManagement || // Account section (permission-based)
      hasSimulations || // Simulations section
      hasReports || // Reports section
      hasScamDetect || // Analysis Tools section
      hasEducationResources || // Education & Resources section
      hasPlatformAdmin; // Platform Admin section (role-based)

    // For desktop: expand panel if user is authenticated and has visible menu sections
    // BUT respect user's manual minimize choice
    if (hasVisibleMenuSections && isCollapsed && !isManuallyMinimized) {
      setIsMinimized(false);
      // Parent will be notified via useEffect
    }
  }, [
    isAuthenticated,
    canAccessAccountManagement,
    isPlatformAdmin,
    canManageGroups,
    isCollapsed,
    isManuallyMinimized,
  ]); // Removed onToggleMinimize from dependencies to prevent infinite loop

  /**
   * User Verification Status Monitor
   * Checks the verification status of the selected user when they change
   * Used to show appropriate UI elements based on verification state
   */
  useEffect(() => {
    const checkVerificationStatus = async () => {
      // Use selectedUser from context
      const userToCheck = selectedUser;

      if (!userToCheck) {
        setUserVerificationStatus(null);
        return;
      }

      setIsCheckingVerification(true);
      try {
        console.log(
          `SidePanel: Checking verification status for user: uuid ${userToCheck.uuid}, userId: ${userToCheck.userId}`,
        );
        const isVerified = await UserAPI.getUserVerificationStatus(
          userToCheck.uuid,
        );
        setUserVerificationStatus(isVerified);
      } catch (error) {
        console.error("SidePanel: Error checking verification status:", error);
        setUserVerificationStatus(null);
      } finally {
        setIsCheckingVerification(false);
      }
    };
    checkVerificationStatus();
  }, [selectedUser]);

  /**
   * Parent Notification Effect
   * Notifies parent component of minimize state changes with proper width values
   * Avoids render conflicts by using ref-based callback
   */
  useEffect(() => {
    // Pass CSS matching widths: 100px when minimized, 320px when expanded
    toggleMinimizeRef.current(isCollapsed ? 100 : 320);
  }, [isCollapsed]);

  // =========================================================================
  // UTILITY FUNCTIONS
  // =========================================================================

  /**
   * Collapses all expandable menu sections
   * Used to ensure only one menu section is expanded at a time
   */
  const collapseAllMenus = () => {
    setIsTopMenuExpanded(false);
    setIsAdminMenuExpanded(false);
    setIsPlatformAdminExpanded(false);
    setIsSimulationsExpanded(false);
    setIsReportsExpanded(false);
    setIsScamDetectAIExpanded(false);
    setIsEducationResourcesExpanded(false);
  };

  /**
   * Toggles the User Management menu expansion state
   */
  /**
   * Toggles the User Management menu expansion state
   */
  const toggleTopMenu = () => {
    setIsTopMenuExpanded(!isTopMenuExpanded);
  };

  /**
   * Toggles the panel minimize/expand state
   * Tracks whether the user manually minimized for UX optimization
   */
  const toggleMinimize = () => {
    setIsHoverExpanded(false);
    setIsMinimized((prev) => {
      const newState = !prev;
      // Track if user is manually minimizing (not expanding)
      setIsManuallyMinimized(newState);
      return newState;
    });
  };

  // Allow external components (e.g. ContentPanel's collapse tab) to trigger the toggle
  useEffect(() => {
    const handleExternalToggle = () => toggleMinimize();
    window.addEventListener("toggleSidePanel", handleExternalToggle);
    return () =>
      window.removeEventListener("toggleSidePanel", handleExternalToggle);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Toggles the Account menu expansion state
   * Only visible to users with account_admin or platform_admin roles
   */
  const toggleAdminMenu = () => {
    setIsAdminMenuExpanded(!isAdminMenuExpanded);
  };

  /**
   * Toggles the Platform Admin menu expansion state
   * Only visible to users with platform_admin role
   */
  const togglePlatformAdminMenu = () => {
    setIsPlatformAdminExpanded(!isPlatformAdminExpanded);
  };

  /**
   * Toggles the Demo menu expansion state
   * Only visible to users with platform_admin role
   */
  const toggleDemoMenu = () => {
    setIsDemoExpanded(!isDemoExpanded);
  };

  /**
   * Toggles the Simulations menu expansion state
   * Available to all authenticated users with role-based item filtering
   */
  const toggleSimulationsMenu = () => {
    setIsSimulationsExpanded(!isSimulationsExpanded);
  };

  /**
   * Toggles the Reports menu expansion state
   * Available to all authenticated users with role-based item filtering
   */
  const toggleReportsMenu = () => {
    setIsReportsExpanded(!isReportsExpanded);
  };

  /**
   * Toggles the Analysis Tools menu expansion state
   * Available to all authenticated users for AI-powered scam detection
   */
  const toggleScamDetectAIMenu = () => {
    setIsScamDetectAIExpanded(!isScamDetectAIExpanded);
  };

  /**
   * Toggles the Education & Resources menu expansion state
   * Available to all authenticated users for educational content
   */
  const toggleEducationResourcesMenu = () => {
    setIsEducationResourcesExpanded(!isEducationResourcesExpanded);
  };

  /**
   * Toggles the Email Protection submenu expansion state
   */
  const toggleEmailProtectionMenu = () => {
    setIsEmailProtectionExpanded(!isEmailProtectionExpanded);
  };

  /**
   * Toggles the Text Protection submenu expansion state
   */
  const toggleTextProtectionMenu = () => {
    setIsTextProtectionExpanded(!isTextProtectionExpanded);
  };

  /**
   * Toggles the Social Protection submenu expansion state
   */
  const toggleSocialProtectionMenu = () => {
    setIsSocialProtectionExpanded(!isSocialProtectionExpanded);
  };

  /**
   * Toggles the More Tools submenu expansion state
   */
  const toggleAdvancedToolsMenu = () => {
    setIsAdvancedToolsExpanded(!isAdvancedToolsExpanded);
  };

  // =========================================================================
  // MENU ITEM LOGIC AND FILTERING
  // =========================================================================

  /**
   * Determines if a menu item should be disabled based on various conditions
   * Used to provide visual feedback when features are unavailable
   *
   * @param {Object} menuItem - The menu item object with name and icon
   * @returns {boolean} True if the menu item should be disabled
   */
  /**
   * Determines if a menu item should be disabled based on various conditions
   * Used to provide visual feedback when features are unavailable
   *
   * @param {Object} menuItem - The menu item object with name and icon
   * @returns {boolean} True if the menu item should be disabled
   */
  const shouldDisableMenuItem = (menuItem) => {
    // Use selectedUser from context
    const currentUser = selectedUser;

    const disabledItems = [];

    // Demo user restrictions
    if (isDemoSeat) {
      const demoDisabledItems = [
        "Forwarded Emails",
        "Forwarded Texts",
        "Resend Verification Key",
      ];
      if (demoDisabledItems.includes(menuItem)) {
        return true;
      }
    }

    // Handle Resend Verification Key specific logic for non-demo users
    if (menuItem === "Resend Verification Key" && !isDemoSeat) {
      // Disable if no user is selected, still checking verification, or user is already verified
      return (
        !currentUser ||
        isCheckingVerification ||
        userVerificationStatus === true
      );
    }

    return disabledItems.includes(menuItem) && !currentUser;
  };

  /**
   * Gets the tooltip content for a menu item, including demo mode restrictions
   *
   * @param {string} itemName - The name of the menu item
   * @param {boolean} isMinimized - Whether the panel is minimized
   * @returns {string} The tooltip content to display
   */
  const getMenuItemTooltip = (itemName, isMinimized) => {
    if (isDemoSeat && shouldDisableMenuItem(itemName)) {
      return "Not Available in Demo Mode";
    }
    return isMinimized ? itemName : "";
  };

  /**
   * Gets the title attribute for non-minimized disabled items
   *
   * @param {string} itemName - The name of the menu item
   * @returns {string|undefined} The title attribute value
   */
  const getMenuItemTitle = (itemName) => {
    if (isDemoSeat && shouldDisableMenuItem(itemName)) {
      return "Not Available in Demo Mode";
    }
    return undefined;
  };

  /**
   * Checks if any child menu item within a submenu is currently active
   * Used to highlight parent menus when their children are selected but collapsed
   *
   * @param {Array} menuItems - Array of menu items to check
   * @returns {boolean} True if any child is active
   */
  const isChildActive = (menuItems) => {
    return menuItems.some((item) => item.name === activeMenuItem);
  };

  /**
   * Checks if any descendant (child or grandchild) within nested submenus is currently active
   * Used for multi-level active state propagation (e.g., Scam Detector -> Email Analysis -> Manual Entry)
   *
   * @returns {boolean} True if any descendant in Scam Detector menu tree is active
   */
  const isScamDetectorDescendantActive = () => {
    const allScamDetectorItems = [
      ...MENU_ITEMS.EMAIL_ANALYSIS_MENU,
      ...MENU_ITEMS.TEXT_ANALYSIS_MENU,
      ...MENU_ITEMS.SOCIAL_PROTECTION_MENU,
      ...MENU_ITEMS.ADVANCED_TOOLS_MENU,
    ];
    return allScamDetectorItems.some((item) => item.name === activeMenuItem);
  };

  /**
   * Gets the appropriate CSS class for menu items based on their enabled/disabled state and active state
   * Applies disabled styling when menu items are not available and active styling for current selection
   *
   * @param {string} menuItem - Name of the menu item
   * @param {string} baseClass - Base CSS class to apply
   * @param {boolean} hasActiveChild - Whether this parent has an active child (for collapsed parents)
   * @returns {string} Complete CSS class string
   */
  const getMenuItemClass = (menuItem, baseClass, hasActiveChild = false) => {
    let classes = baseClass;

    if (shouldDisableMenuItem(menuItem)) {
      classes += " disabled";
    }

    if (activeMenuItem === menuItem || hasActiveChild) {
      classes += " active";
    }

    return classes;
  };

  /**
   * Updates the active menu item state and hero content
   * This function centralizes the logic for switching content and tracking active state
   *
   * @param {string} menuItem - Name of the menu item to activate
   * @param {string} heroContent - Content to display in Hero section (optional, defaults to menuItem)
   */
  const handleMenuItemActivation = (menuItem, heroContent = null) => {
    const contentToSet = heroContent || menuItem;
    console.log(
      `SidePanel: Activating menu item "${menuItem}" with content "${contentToSet}"`,
    );
    setActiveMenuItem(menuItem);
    setHeroContent(contentToSet);
    setIsHoverExpanded(false);

    if (onMenuSelect) {
      onMenuSelect(menuItem);
    }
  };

  /**
   * Handles clicks on User Management menu items
   * Includes special logic for verification-related actions
   *
   * @param {string} menuItem - Name of the clicked menu item
   */
  const handleTopMenuItemClick = async (menuItem) => {
    // Check if menu item should be disabled
    if (shouldDisableMenuItem(menuItem)) {
      return; // Don't execute if disabled
    }

    // Handle Resend Verification Key functionality
    if (menuItem === "Resend Verification Key") {
      // Use selectedUser from context
      const currentUser = selectedUser;

      if (!currentUser) {
        console.error("SidePanel: No user selected for verification resend");
        return;
      }
      try {
        console.log(
          "SidePanel: Resending verification email for user: Id: ",
          currentUser.userId,
          ", email: ",
          currentUser.email,
        );
        await ConsentAPI.resendVerificationEmail(
          currentUser.userId,
          currentUser.email,
        );
        toast.success(
          `Verification email has been sent to ${currentUser.email}`,
        );

        // Refresh verification status
        const isVerified = await UserAPI.getUserVerificationStatus(
          currentUser.userId,
        );
        setUserVerificationStatus(isVerified);
      } catch (error) {
        console.error("SidePanel: Error resending verification email:", error);
        toast.error(t("sidePanel.toast.verificationEmailFailed"));
      }
      return;
    }

    // Default handling for other menu items
    console.log("SidePanel: Selected menu item:", menuItem); // Debugging line
    if (menuItem === "User List") {
      handleMenuItemActivation(menuItem, "UserList");
    } else {
      handleMenuItemActivation(menuItem);
    }
  };

  /**
   * Handles clicks on Simulations menu items
   * Updates the main content area with the selected simulation component
   *
   * @param {string} menuItem - Name of the clicked simulation menu item
   */
  const handleSimulationsItemClick = (menuItem) => {
    console.log("SidePanel: Selected simulations menu item:", menuItem); // Debugging line
    handleMenuItemActivation(menuItem);
  };

  /**
   * Handles clicks on Reports menu items
   * Updates the main content area with the selected reports component
   *
   * @param {string} menuItem - Name of the clicked reports menu item
   */
  const handleReportsItemClick = (menuItem) => {
    console.log("SidePanel: Selected reports menu item:", menuItem); // Debugging line
    handleMenuItemActivation(menuItem);
  };

  /**
   * Handles clicks on Account menu items
   * Updates the main content area with the selected admin component
   * Includes special handling for legacy menu mappings
   *
   * @param {string} menuItem - Name of the clicked admin menu item
   */
  const handleAdminMenuItemClick = (menuItem) => {
    console.log("SidePanel: Selected admin menu item:", menuItem); // Debugging line

    // Handle special case for legacy Subscriptions and Payments
    if (
      menuItem === "Subscriptions and Payments" ||
      menuItem === "Subscriptions" ||
      menuItem === "Payments"
    ) {
      handleMenuItemActivation(menuItem, "Account & Billing");
    } else {
      handleMenuItemActivation(menuItem);
    }
  };

  /**
   * Handles clicks on Platform Admin menu items
   * Includes special handling for coming soon features and complex operations
   * Only accessible to users with platform_admin role
   *
   * @param {string} menuItem - Name of the clicked platform admin menu item
   */
  const handlePlatformAdminItemClick = async (menuItem) => {
    console.log("SidePanel: Selected platform admin menu item:", menuItem); // Debugging line

    // Handle features that are coming soon
    if (menuItem === "Send SMS") {
      handleMenuItemActivation(menuItem, `Coming Soon:${menuItem}`);
      return;
    }

    // Handle admin dashboard
    if (menuItem === "Admin Dashboard") {
      try {
        handleMenuItemActivation(menuItem);
        return;
      } catch (error) {
        console.error("Error handling Admin Dashboard:", error);
        toast.error(t("sidePanel.toast.adminDashboardFailed"));
        return;
      }
    }

    // Handle simulation schedule creation
    if (menuItem === "Create Simulation Schedule") {
      try {
        // For now, just navigate to the component. The actual API call will be handled by the component itself
        handleMenuItemActivation(menuItem);
        return;
      } catch (error) {
        console.error("Error handling Create Simulation Schedule:", error);
        toast.error(
          "Failed to access Create Simulation Schedule. Please try again.",
        );
        return;
      }
    }

    // Handle simulation schedule execution
    if (menuItem === "Execute Simulation Schedule") {
      try {
        // For now, just navigate to the component. The actual API call will be handled by the component itself
        handleMenuItemActivation(menuItem);
        return;
      } catch (error) {
        console.error("Error handling Execute Simulation Schedule:", error);
        toast.error(
          "Failed to access Execute Simulation Schedule. Please try again.",
        );
        return;
      }
    }

    // Handle blog generation
    if (menuItem === "Generate Blogs") {
      try {
        handleMenuItemActivation(menuItem);
        return;
      } catch (error) {
        console.error("Error handling Generate Blogs:", error);
        toast.error(t("sidePanel.toast.generateBlogsFailed"));
        return;
      }
    }

    // Handle simulation data generator
    if (menuItem === "Generate Simulation Data") {
      try {
        handleMenuItemActivation(menuItem);
        return;
      } catch (error) {
        console.error("Error handling Generate Simulation Data:", error);
        toast.error(
          "Failed to access Generate Simulation Data. Please try again.",
        );
        return;
      }
    }

    // Handle other platform admin menu items
    handleMenuItemActivation(menuItem);
  };

  /**
   * Handles clicks on Demo menu items
   * Navigates to the Demo Users admin panel
   *
   * @param {string} menuItem - Name of the clicked menu item
   */
  const handleDemoItemClick = (menuItem) => {
    console.log("SidePanel: Selected demo menu item:", menuItem);
    handleMenuItemActivation(menuItem);
  };

  /**
   * Handles clicks on Analysis Tools menu items
   * Includes special mapping for backward compatibility with legacy components
   * Available to all authenticated users
   *
   * @param {string} menuItem - Name of the clicked menu item
   */
  const handleScamDetectAIItemClick = (menuItem) => {
    console.log("SidePanel: Selected analysis tools menu item:", menuItem); // Debugging line

    // Pass the menu item names directly to Hero - Hero.js handles all cases
    handleMenuItemActivation(menuItem);
  };

  /**
   * Handles clicks on Education & Resources menu items
   * Updates the main content area with the selected educational component
   *
   * @param {string} menuItem - Name of the clicked menu item
   */
  const handleEducationResourcesItemClick = (menuItem) => {
    console.log(
      "SidePanel: Selected education & resources menu item:",
      menuItem,
    ); // Debugging line
    handleMenuItemActivation(menuItem);
  };

  /**
   * Generic menu item click handler
   * Used as fallback for menu items without specific handling logic
   *
   * @param {string} menuItem - Name of the clicked menu item
   */
  const handleMenuClick = (menuItem) => {
    console.log("SidePanel: Selected menu item:", menuItem);
    handleMenuItemActivation(menuItem);
  };

  // =========================================================================
  // ROLE-BASED CONDITIONAL RENDERING LOGIC
  // =========================================================================

  /**
   * Determines if current user has limited permissions (group_member only)
   * Users with only group_member role get simplified UI with direct menu access
   * This improves UX by removing unnecessary navigation complexity
   *
   * @returns {boolean} True if user has only basic group member permissions
   */
  const isGroupMemberOnly = () => {
    return (
      !isGroupAdmin() && !isPlatformAdmin() && !canAccessAccountManagement()
    );
  };

  /**
   * Gets filtered menu items based on user's role permissions
   * Removes menu items that the current user doesn't have access to
   *
   * @param {Array} menuItems - Array of menu items to filter
   * @param {string} menuType - Type of menu for role-specific filtering logic
   * @returns {Array} Filtered array of menu items accessible to current user
   */
  const getFilteredMenuItems = (menuItems, menuType) => {
    return menuItems.filter((item) => {
      // Apply role-based filtering based on menu type and item name
      switch (menuType) {
        case "USER_MENU":
          // Groups & Organizations - Platform admin only
          if (item.name === "Groups & Organizations") {
            return isPlatformAdmin();
          }
          // Resend Verification Key - Group admin and above
          if (item.name === "Resend Verification Key") {
            return (
              isGroupAdmin() ||
              canAccessAccountManagement() ||
              isPlatformAdmin()
            );
          }
          // Member User Info - Always available to authenticated users
          return true;

        case "SIMULATIONS_MENU":
          // Simulation Messages - Platform admin only
          if (item.name === "Simulation Messages") {
            return isPlatformAdmin();
          }
          // Other simulation items available to all authenticated users
          return true;

        case "REPORTS_MENU":
          // Shield Reports - Available to all authenticated users
          if (item.name === "Shield Reports") {
            return true;
          }
          // Cyber-Insurance Readiness - Corporate account admins only.
          // This is a corporate-only report, so it is gated purely on the
          // account being corporate (no platform-admin override) to match the
          // "Account Profile" menu item — otherwise a platform admin would see
          // it on an Individual/Family account where it doesn't apply.
          if (item.name === "Cyber-Insurance Readiness") {
            return isCorporate && canAccessAccountManagement();
          }
          // Export Data - Account admin and above
          if (item.name === "Export Data") {
            return canAccessAccountManagement() || isPlatformAdmin();
          }
          // All other report items - Available to all authenticated users
          return true;

        case "ADMIN_MENU":
          // All admin menu items require account_admin or platform_admin role
          return canAccessAccountManagement() || isPlatformAdmin();

        case "PLATFORM_ADMIN_MENU":
          // Platform admin menu requires platform_admin role
          return isPlatformAdmin();

        case "SCAM_DETECT_AI_MENU":
          // AI scam detection available to all authenticated users
          return true;

        case "EMAIL_ANALYSIS_MENU":
          // Email Analysis submenu - available to all authenticated users
          // Demo users will see "Forwarded Emails" but it will be disabled
          return true;

        case "TEXT_ANALYSIS_MENU":
          // Text/SMS Analysis submenu - available to all authenticated users
          // Demo users will see "Forwarded Texts" but it will be disabled
          return true;

        case "SOCIAL_PROTECTION_MENU":
        case "SOCIAL_PROTECTION_MENU":
          // Social Media Protection submenu - available to all authenticated users
          return true;

        case "ADVANCED_TOOLS_MENU":
          // More Tools - available to all authenticated users
          return true;
        case "EDUCATION_RESOURCES_MENU":
          // Education & Resources available to all authenticated users
          return true;

        default:
          return true;
      }
    });
  };

  // =========================================================================
  // RENDER COMPONENT
  // =========================================================================

  return (
    <div
      className={`side-panel ${isMinimized ? "minimized" : ""} ${
        isCollapsed && isHoverExpanded ? "hover-expanded" : ""
      }`}
      style={{
        display: window.innerWidth < 768 ? "none" : "flex",
      }}
      onMouseEnter={() => isCollapsed && setIsHoverExpanded(true)}
      onMouseLeave={() => setIsHoverExpanded(false)}
    >
      {isSessionTimedOut ? (
        // Show empty content when session has timed out
        <div className="session-timeout-empty">
          <p>Please log in to access the application</p>
        </div>
      ) : isAuthenticated ? (
        !canAccessFunctionalFeatures && !isInGracePeriod ? (
          // Show message when authenticated but cannot access functional features
          <div className="no-subscription-message">
            <p>{getAccessDenialReason || t("deactivated.accessRestricted")}</p>
          </div>
        ) : (
          <>
            <div className="side-panel-scroll-area">
              {isInGracePeriod && (
                <div className="sidepanel-grace-banner" role="status">
                  <div className="sidepanel-grace-banner-title">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{t("ab.grace.title")}</span>
                  </div>
                  <p>
                    {graceEndsOn
                      ? t(
                          canManageBilling
                            ? "ab.grace.message.withDate"
                            : "ab.grace.nonOwner.withDate",
                        ).replace("{date}", graceEndsOn)
                      : t(
                          canManageBilling
                            ? "ab.grace.message.noDate"
                            : "ab.grace.nonOwner.noDate",
                        )}
                  </p>
                  {canManageBilling && (
                    <button
                      type="button"
                      className="sidepanel-grace-banner-cta"
                      onClick={() =>
                        handleAdminMenuItemClick("Account & Billing")
                      }
                    >
                      {t("ab.grace.cta.updatePayment")}
                    </button>
                  )}
                </div>
              )}

              {/* MENU REORG - User Management menu temporarily commented out */}
              {/* ================================================= */}
              {/* USER MANAGEMENT MENU SECTION */}
              {/* ================================================= */}
              {/* <div>
              {!isGroupAdmin() &&
              !isPlatformAdmin() &&
              !canAccessAccountManagement() ? (
                <h4
                  onClick={() => handleTopMenuItemClick("Member User Info")}
                  className={getMenuItemClass(
                    "Member User Info",
                    "first-top-level-menu-item top-level-menu-item direct-menu-item"
                  )}
                  title={isMinimized ? "Member User Info" : ""}
                >
                  <FontAwesomeIcon className="icon" icon={faUserCog} />
                  {!isMinimized ? "Member User Info" : ""}
                </h4>
              ) : (
                <>
                  <h4
                    onClick={toggleTopMenu}
                    className="first-top-level-menu-item top-level-menu-item"
                    title={isMinimized ? "User Management" : ""}
                  >
                    <FontAwesomeIcon className="icon" icon={faUserCog} />
                    {!isMinimized ? "User Management" : ""}
                    <span
                      className={`caret ${isTopMenuExpanded ? "expanded" : ""}`}
                    ></span>
                  </h4>
                  {isTopMenuExpanded && (
                    <ul>
                      {MENU_ITEMS.USER_MENU.filter((item) => {
                        if (item.name === "Groups & Organizations") {
                          return isPlatformAdmin();
                        }
                        if (item.name === "Resend Verification Key") {
                          return isGroupAdmin();
                        }
                        return true;
                      }).map((item) => (
                        <Tooltip
                          key={item.name}
                          content={getMenuItemTooltip(item.name, isMinimized)}
                          position="right"
                          disabled={!isMinimized && !shouldDisableMenuItem(item.name)}
                        >
                          <li
                            className={getMenuItemClass(item.name, "menu-item")}
                            onClick={() => {
                              if (!shouldDisableMenuItem(item.name)) {
                                handleTopMenuItemClick(item.name);
                              }
                            }}
                            title={getMenuItemTitle(item.name)}
                            style={{
                              cursor: shouldDisableMenuItem(item.name) ? "not-allowed" : "pointer"
                            }}
                          >
                            <FontAwesomeIcon className="icon" icon={item.icon} />
                            {!isMinimized ? item.name : ""}
                          </li>
                        </Tooltip>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div> */}
              {/* ================================================= */}
              {/* SCAM DETECTOR MENU SECTION - RESTRUCTURED */}
              {/* ================================================= */}
              <div>
                <Tooltip
                  content={t("Scam Detector")}
                  position="right"
                  disabled={!isMinimized}
                >
                  <h4
                    onClick={toggleScamDetectAIMenu}
                    className={`first-top-level-menu-item top-level-menu-item ${
                      !isScamDetectAIExpanded &&
                      isScamDetectorDescendantActive()
                        ? "active"
                        : ""
                    }`}
                  >
                    <FontAwesomeIcon className="icon" icon={faShield} />
                    {!isMinimized ? t("Scam Detector") : ""}
                    <span
                      className={`caret ${
                        isScamDetectAIExpanded ? "expanded" : ""
                      }`}
                    ></span>
                  </h4>
                </Tooltip>
                {isScamDetectAIExpanded && (
                  <ul>
                    {/* Email Analysis Submenu */}
                    <Tooltip
                      content={t("Email Analysis")}
                      position="right"
                      disabled={!isMinimized}
                    >
                      <li
                        className={`${getMenuItemClass(
                          "Email Analysis",
                          "menu-item submenu-parent",
                          !isEmailProtectionExpanded &&
                            isChildActive(MENU_ITEMS.EMAIL_ANALYSIS_MENU),
                        )} ${isEmailProtectionExpanded ? "expanded" : ""}`}
                        onClick={toggleEmailProtectionMenu}
                      >
                        <FontAwesomeIcon className="icon" icon={faEnvelope} />
                        {!isMinimized ? t("Email Analysis") : ""}
                        <span
                          className={`caret ${
                            isEmailProtectionExpanded ? "expanded" : ""
                          }`}
                        ></span>
                      </li>
                    </Tooltip>
                    {isEmailProtectionExpanded && (
                      <ul className="submenu">
                        {getFilteredMenuItems(
                          MENU_ITEMS.EMAIL_ANALYSIS_MENU,
                          "EMAIL_ANALYSIS_MENU",
                        ).map((item) => (
                          <Tooltip
                            key={item.name}
                            content={getMenuItemTooltip(item.name, isMinimized)}
                            position="right"
                            disabled={
                              !isMinimized && !shouldDisableMenuItem(item.name)
                            }
                          >
                            <li
                              className={getMenuItemClass(
                                item.name,
                                "submenu-item",
                              )}
                              onClick={() => {
                                if (!shouldDisableMenuItem(item.name)) {
                                  handleScamDetectAIItemClick(item.name);
                                }
                              }}
                              title={undefined}
                              style={{
                                cursor: shouldDisableMenuItem(item.name)
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              {isMinimized && (
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={item.icon}
                                />
                              )}
                              {!isMinimized ? t(item.name) : ""}
                            </li>
                          </Tooltip>
                        ))}
                      </ul>
                    )}

                    {/* Text/SMS Analysis Submenu */}
                    <Tooltip
                      content={t("Text/SMS Analysis")}
                      position="right"
                      disabled={!isMinimized}
                    >
                      <li
                        className={`${getMenuItemClass(
                          "Text/SMS Analysis",
                          "menu-item submenu-parent",
                          !isTextProtectionExpanded &&
                            isChildActive(MENU_ITEMS.TEXT_ANALYSIS_MENU),
                        )} ${isTextProtectionExpanded ? "expanded" : ""}`}
                        onClick={toggleTextProtectionMenu}
                      >
                        <FontAwesomeIcon className="icon" icon={faSms} />
                        {!isMinimized ? t("Text/SMS Analysis") : ""}
                        <span
                          className={`caret ${
                            isTextProtectionExpanded ? "expanded" : ""
                          }`}
                        ></span>
                      </li>
                    </Tooltip>
                    {isTextProtectionExpanded && (
                      <ul className="submenu">
                        {getFilteredMenuItems(
                          MENU_ITEMS.TEXT_ANALYSIS_MENU,
                          "TEXT_ANALYSIS_MENU",
                        ).map((item) => (
                          <Tooltip
                            key={item.name}
                            content={
                              item.disabled
                                ? `${item.name} (Coming soon)`
                                : getMenuItemTooltip(item.name, isMinimized)
                            }
                            position="right"
                            disabled={
                              !isMinimized &&
                              !shouldDisableMenuItem(item.name) &&
                              !item.disabled
                            }
                          >
                            <li
                              className={`${getMenuItemClass(
                                item.name,
                                "submenu-item",
                              )}${item.disabled ? " disabled" : ""}`}
                              onClick={() => {
                                if (
                                  !item.disabled &&
                                  !shouldDisableMenuItem(item.name)
                                ) {
                                  handleScamDetectAIItemClick(item.name);
                                }
                              }}
                              title={
                                item.disabled
                                  ? `${item.name} (Coming soon)`
                                  : undefined
                              }
                              style={{
                                cursor:
                                  shouldDisableMenuItem(item.name) ||
                                  item.disabled
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {isMinimized && (
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={item.icon}
                                />
                              )}
                              {!isMinimized ? t(item.name) : ""}
                            </li>
                          </Tooltip>
                        ))}
                      </ul>
                    )}

                    {/* Social Media Submenu */}
                    <Tooltip
                      content={t("Social Media")}
                      position="right"
                      disabled={!isMinimized}
                    >
                      <li
                        className={`${getMenuItemClass(
                          "Social Media",
                          "menu-item submenu-parent",
                          !isSocialProtectionExpanded &&
                            isChildActive(MENU_ITEMS.SOCIAL_PROTECTION_MENU),
                        )} ${isSocialProtectionExpanded ? "expanded" : ""}`}
                        onClick={toggleSocialProtectionMenu}
                      >
                        <FontAwesomeIcon className="icon" icon={faShareAlt} />
                        {!isMinimized ? t("Social Media") : ""}
                        <span
                          className={`caret ${
                            isSocialProtectionExpanded ? "expanded" : ""
                          }`}
                        ></span>
                      </li>
                    </Tooltip>
                    {isSocialProtectionExpanded && (
                      <ul className="submenu">
                        {MENU_ITEMS.SOCIAL_PROTECTION_MENU.map((item) => (
                          <Tooltip
                            key={item.name}
                            content={item.name}
                            position="right"
                            disabled={!isMinimized}
                          >
                            <li
                              className={getMenuItemClass(
                                item.name,
                                "submenu-item",
                              )}
                              onClick={() =>
                                handleScamDetectAIItemClick(item.name)
                              }
                            >
                              {isMinimized && (
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={item.icon}
                                />
                              )}
                              {!isMinimized ? t(item.name) : ""}
                            </li>
                          </Tooltip>
                        ))}
                      </ul>
                    )}

                    {/* More Tools Submenu */}
                    <Tooltip
                      content={t("More Tools")}
                      position="right"
                      disabled={!isMinimized}
                    >
                      <li
                        className={`${getMenuItemClass(
                          "More Tools",
                          "menu-item submenu-parent",
                          !isAdvancedToolsExpanded &&
                            isChildActive(MENU_ITEMS.ADVANCED_TOOLS_MENU),
                        )} ${isAdvancedToolsExpanded ? "expanded" : ""}`}
                        onClick={toggleAdvancedToolsMenu}
                      >
                        <FontAwesomeIcon className="icon" icon={faToolbox} />
                        {!isMinimized ? t("More Tools") : ""}
                        <span
                          className={`caret ${
                            isAdvancedToolsExpanded ? "expanded" : ""
                          }`}
                        ></span>
                      </li>
                    </Tooltip>
                    {isAdvancedToolsExpanded && (
                      <ul className="submenu">
                        {MENU_ITEMS.ADVANCED_TOOLS_MENU.map((item) => (
                          <Tooltip
                            key={item.name}
                            content={
                              item.disabled
                                ? `${item.name} (Coming soon)`
                                : item.name
                            }
                            position="right"
                            disabled={!isMinimized}
                          >
                            <li
                              className={`${getMenuItemClass(
                                item.name,
                                "submenu-item",
                              )}${item.disabled ? " disabled" : ""}`}
                              onClick={() =>
                                !item.disabled &&
                                handleScamDetectAIItemClick(item.name)
                              }
                            >
                              {isMinimized && (
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={item.icon}
                                />
                              )}
                              {!isMinimized ? t(item.name) : ""}
                            </li>
                          </Tooltip>
                        ))}
                      </ul>
                    )}
                  </ul>
                )}
              </div>
              {/* Spacer between Scam Detector and Simulations */}
              <div className="menu-spacer"></div>
              {/* ================================================= */}
              {/* SIMULATIONS MENU SECTION */}
              {/* ================================================= */}
              <div>
                {/* 
                CONDITIONAL SIMULATIONS UI:
                - Group members only: Direct access to "Simulation Profile"
                - Admin users: Expandable "Simulations" menu with multiple options
              */}
                {!isGroupAdmin() &&
                !isPlatformAdmin() &&
                !canAccessAccountManagement() ? (
                  /* 
                  DIRECT ACCESS FOR GROUP MEMBERS:
                  Most group members primarily need access to their simulation profile,
                  so provide direct access without menu expansion complexity.
                */
                  <Tooltip
                    content={t("Simulation Profile")}
                    position="right"
                    disabled={!isMinimized}
                  >
                    <h4
                      onClick={() =>
                        handleSimulationsItemClick("Simulation Profile")
                      }
                      className={getMenuItemClass(
                        "Simulation Profile",
                        "top-level-menu-item direct-menu-item",
                      )}
                    >
                      <FontAwesomeIcon className="icon" icon={faFish} />
                      {!isMinimized ? t("Simulation Profile") : ""}
                    </h4>
                  </Tooltip>
                ) : (
                  /* 
                  EXPANDABLE SIMULATIONS MENU FOR ADMINS:
                  Admin users get access to additional simulation management features
                  organized in an expandable menu section.
                */
                  <>
                    <Tooltip
                      content={t("Simulations")}
                      position="right"
                      disabled={!isMinimized}
                    >
                      <h4
                        onClick={toggleSimulationsMenu}
                        className={`top-level-menu-item ${
                          !isSimulationsExpanded &&
                          isChildActive(MENU_ITEMS.SIMULATIONS_MENU)
                            ? "active"
                            : ""
                        }`}
                      >
                        <FontAwesomeIcon className="icon" icon={faFish} />
                        {!isMinimized ? t("Simulations") : ""}
                        <span
                          className={`caret ${
                            isSimulationsExpanded ? "expanded" : ""
                          }`}
                        ></span>
                      </h4>
                    </Tooltip>
                    {isSimulationsExpanded && (
                      <ul>
                        {MENU_ITEMS.SIMULATIONS_MENU.filter((item) => {
                          // Filter out "Simulation Messages" if user doesn't have platform admin role
                          if (item.name === "Simulation Messages") {
                            return isPlatformAdmin();
                          }
                          return true;
                        }).map((item) => (
                          <Tooltip
                            key={item.name}
                            content={item.name}
                            position="right"
                            disabled={!isMinimized}
                          >
                            <li
                              className={getMenuItemClass(
                                item.name,
                                "menu-item",
                              )}
                              onClick={() =>
                                handleSimulationsItemClick(item.name)
                              }
                            >
                              {isMinimized && (
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={item.icon}
                                />
                              )}
                              {!isMinimized ? t(item.name) : ""}
                            </li>
                          </Tooltip>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
              {/* Spacer between Simulations and Resources */}
              <div className="menu-spacer"></div>
              {/* ================================================= */}
              {/* RESOURCES MENU SECTION */}
              {/* ================================================= */}
              <div>
                <Tooltip
                  content={t("Trainings & Alerts")}
                  position="right"
                  disabled={!isMinimized}
                >
                  <h4
                    onClick={toggleEducationResourcesMenu}
                    className={`top-level-menu-item ${
                      !isEducationResourcesExpanded &&
                      isChildActive(MENU_ITEMS.EDUCATION_RESOURCES_MENU)
                        ? "active"
                        : ""
                    }`}
                  >
                    <FontAwesomeIcon className="icon" icon={faGraduationCap} />
                    {!isMinimized ? t("Trainings & Alerts") : ""}
                    <span
                      className={`caret ${
                        isEducationResourcesExpanded ? "expanded" : ""
                      }`}
                    ></span>
                  </h4>
                </Tooltip>
                {isEducationResourcesExpanded && (
                  <ul>
                    {MENU_ITEMS.EDUCATION_RESOURCES_MENU.map((item) => {
                      // Enable Training Center and Alerts Intelligence Hub, disable others (Coming Soon)
                      const isDisabled =
                        item.name !== "Training Center" &&
                        item.name !== "Alerts Intelligence Hub";
                      return (
                        <Tooltip
                          key={item.name}
                          content={
                            isDisabled
                              ? `${item.name} (Coming Soon)`
                              : item.name
                          }
                          position="right"
                          disabled={!isMinimized}
                        >
                          <li
                            className={`${getMenuItemClass(item.name, "menu-item")}${isDisabled ? " disabled" : ""}`}
                            onClick={() =>
                              !isDisabled &&
                              handleEducationResourcesItemClick(item.name)
                            }
                            style={
                              isDisabled
                                ? { cursor: "not-allowed", opacity: 0.5 }
                                : {}
                            }
                          >
                            {isMinimized && (
                              <FontAwesomeIcon
                                className="icon"
                                icon={item.icon}
                              />
                            )}
                            {!isMinimized ? t(item.name) : ""}
                          </li>
                        </Tooltip>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* ACCOUNT MENU SECTION - TOP LEVEL - Only show if user has permission */}
              {/* {canAccessAccountManagement() && (
              <div>
                <h4
                  onClick={toggleAdminMenu}
                  className="top-level-menu-item"
                  title={isMinimized ? "Account" : ""}
                >
                  <FontAwesomeIcon className="icon" icon={faUserShield} />
                  {!isMinimized ? "Account" : ""}
                  <span
                    className={`caret ${isAdminMenuExpanded ? "expanded" : ""}`}
                  ></span>
                </h4>{" "}
                {isAdminMenuExpanded && (
                  <ul>
                    {MENU_ITEMS.ADMIN_MENU.map((item) => (
                      <li
                        key={item.name}
                        className={getMenuItemClass(item.name, "menu-item")}
                        onClick={() => handleAdminMenuItemClick(item.name)}
                        title={isMinimized ? item.name : ""}
                      >
                        <FontAwesomeIcon className="icon" icon={item.icon} />
                        {!isMinimized ? item.name : ""}
                      </li>
                    ))}{" "}
                  </ul>
                )}{" "}
              </div>
            )}{" "} */}
              {/* Spacer between Resources and Reports */}
              <div className="menu-spacer"></div>
              {/* REPORTS MENU SECTION - TOP LEVEL */}
              <div>
                <Tooltip
                  content={t("Reports")}
                  position="right"
                  disabled={!isMinimized}
                >
                  <h4
                    onClick={toggleReportsMenu}
                    className={`top-level-menu-item ${
                      !isReportsExpanded &&
                      isChildActive(MENU_ITEMS.REPORTS_MENU)
                        ? "active"
                        : ""
                    }`}
                  >
                    <FontAwesomeIcon className="icon" icon={faChartBar} />
                    {!isMinimized ? t("Reports") : ""}
                    <span
                      className={`caret ${isReportsExpanded ? "expanded" : ""}`}
                    ></span>
                  </h4>
                </Tooltip>
                {isReportsExpanded && (
                  <ul>
                    {MENU_ITEMS.REPORTS_MENU.map((item) => {
                      // Cyber-Insurance Readiness is a corporate-only report.
                      // Gate it purely on the account being corporate (matching
                      // the "Account Profile" menu item) so it never appears on
                      // an Individual/Family account, even for platform admins.
                      if (item.name === "Cyber-Insurance Readiness") {
                        if (!(isCorporate && canAccessAccountManagement())) {
                          return null;
                        }
                      }

                      // Check if this item requires account management access
                      const accountAdminOnlyItems = ["Export Data"];
                      const requiresAccountAdmin =
                        accountAdminOnlyItems.includes(item.name);

                      // If the item requires account admin access but user doesn't have account management, don't render it
                      if (
                        requiresAccountAdmin &&
                        !canAccessAccountManagement()
                      ) {
                        return null;
                      }

                      return (
                        <Tooltip
                          key={item.name}
                          content={item.name}
                          position="right"
                          disabled={!isMinimized}
                        >
                          <li
                            className={getMenuItemClass(item.name, "menu-item")}
                            onClick={() => handleReportsItemClick(item.name)}
                          >
                            {isMinimized && (
                              <FontAwesomeIcon
                                className="icon"
                                icon={item.icon}
                              />
                            )}
                            {!isMinimized ? t(item.name) : ""}
                          </li>
                        </Tooltip>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* Spacer between Reports and Platform Admin */}
              <div className="menu-spacer"></div>
              {/* PLATFORM ADMIN MENU SECTION - TOP LEVEL - Only show if user has platform_admin role */}
              {isPlatformAdmin() && (
                <div>
                  <Tooltip
                    content={t("Platform Admin")}
                    position="right"
                    disabled={!isMinimized}
                  >
                    <h4
                      onClick={togglePlatformAdminMenu}
                      className="top-level-menu-item"
                    >
                      <FontAwesomeIcon className="icon" icon={faCrown} />
                      {!isMinimized ? t("Platform Admin") : ""}
                      <span
                        className={`caret ${
                          isPlatformAdminExpanded ? "expanded" : ""
                        }`}
                      ></span>
                    </h4>
                  </Tooltip>
                  {isPlatformAdminExpanded && (
                    <ul>
                      {MENU_ITEMS.PLATFORM_ADMIN_MENU.map((item) => (
                        <Tooltip
                          key={item.name}
                          content={item.name}
                          position="right"
                          disabled={!isMinimized}
                        >
                          <li
                            className={getMenuItemClass(item.name, "menu-item")}
                            onClick={() =>
                              handlePlatformAdminItemClick(item.name)
                            }
                          >
                            {isMinimized && (
                              <FontAwesomeIcon
                                className="icon"
                                icon={item.icon}
                              />
                            )}
                            {!isMinimized ? t(item.name) : ""}
                          </li>
                        </Tooltip>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {/* DEMO MENU SECTION - Only show if user has platform_admin role */}
              {isPlatformAdmin() && (
                <div>
                  <Tooltip
                    content={t("Demo")}
                    position="right"
                    disabled={!isMinimized}
                  >
                    <h4
                      onClick={toggleDemoMenu}
                      className="top-level-menu-item"
                    >
                      <FontAwesomeIcon className="icon" icon={faUserShield} />
                      {!isMinimized ? t("Demo") : ""}
                      <span
                        className={`caret ${isDemoExpanded ? "expanded" : ""}`}
                      ></span>
                    </h4>
                  </Tooltip>
                  {isDemoExpanded && (
                    <ul>
                      {MENU_ITEMS.DEMO_MENU.map((item) => (
                        <Tooltip
                          key={item.name}
                          content={item.name}
                          position="right"
                          disabled={!isMinimized}
                        >
                          <li
                            className={getMenuItemClass(item.name, "menu-item")}
                            onClick={() => handleDemoItemClick(item.name)}
                          >
                            {isMinimized && (
                              <FontAwesomeIcon
                                className="icon"
                                icon={item.icon}
                              />
                            )}
                            {!isMinimized ? t(item.name) : ""}
                          </li>
                        </Tooltip>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )
      ) : (
        // Show sign-in prompt for unauthenticated users
        <div className="sign-in-message">
          <p>{t("nav.signInPrompt")}</p>
        </div>
      )}
    </div>
  );
}

/**
 * COMPONENT SUMMARY:
 *
 * The SidePanel component is a sophisticated navigation sidebar that adapts to user roles
 * and device characteristics. Key features include:
 *
 * 1. ROLE-BASED ACCESS CONTROL:
 *    - group_member: Simplified UI with direct menu access
 *    - group_admin: Access to user management and verification features
 *    - account_admin: Additional account management capabilities
 *    - platform_admin: Full access to all features and administrative tools
 *
 * 2. RESPONSIVE DESIGN:
 *    - Auto-minimizes on mobile devices (width < 768px)
 *    - Hides completely on phone form factor for better UX
 *    - Expandable/collapsible sections for admin users
 *
 * 3. UX OPTIMIZATIONS:
 *    - Direct menu items for users with limited permissions
 *    - Expandable menus for users with multiple options
 *    - Visual feedback for disabled/unavailable features
 *    - Proper spacing and visual hierarchy
 *
 * 4. INTEGRATION POINTS:
 *    - UserContext for user state management
 *    - useRoleCheck hook for permission validation
 *    - Hero component integration for content switching
 *    - localStorage for deep linking support
 *
 * MAINTENANCE NOTES:
 * - Menu item names must match component names in Hero.js
 * - Role checking logic should be kept in sync with backend permissions
 * - CSS classes follow BEM-like conventions for maintainability
 * - Event handlers include error handling and user feedback
 */

export default SidePanel;
